import React from 'react';
import { ArrowRight, CheckCircle2, Leaf, Activity, Heart, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';

const NutritionnisteVeveyClarens = () => {
  return (
    <>
      <SEO 
        title="Nutritionniste à Vevey & Clarens | Rééquilibrage alimentaire"
        description="Nutritionniste à Vevey et Clarens (Montreux). Accompagnement personnalisé pour perte de poids, troubles digestifs et équilibre hormonal. Cabinet médical."
        keywords="nutritionniste Vevey, nutritionniste Clarens, nutritionniste Montreux, rééquilibrage alimentaire, diététicienne Riviera, consultation nutrition Vevey"
        url="https://nutricentreriviera.ch/nutritionniste-vevey-clarens"
      />
      
      <div style={{ marginTop: '100px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--gradient-hero)',
          padding: '4rem 0',
          textAlign: 'center'
        }}>
          <div className="container">
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
              Nutritionniste à Vevey & Clarens
            </h1>
            <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
              Accompagnement personnalisé pour améliorer votre alimentation et atteindre vos objectifs de santé
            </p>
            <a 
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Prendre rendez-vous
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        {/* Main Content */}
        <section className="section" style={{ background: 'white' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            {/* Introduction */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: 'var(--accent-primary)' }}>
                Nutritionniste à Vevey et Clarens — Accompagnement sur mesure
              </h2>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                <strong>Nutritionniste à Vevey et Clarens (Montreux)</strong>, je propose un accompagnement personnalisé 
                pour améliorer votre alimentation et atteindre vos objectifs de santé.
              </p>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                Chaque programme est adapté à votre <strong>métabolisme</strong>, votre mode de vie et vos besoins spécifiques.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                Consultations en cabinet à <strong>Vevey</strong> et à <strong>Clarens</strong>, dans un environnement médical professionnel.
              </p>
            </div>

            {/* Accompagnement pour */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Accompagnement nutritionnel pour
              </h2>
              <div style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--border-light)'
              }}>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Perte de poids</strong> — Programme personnalisé pour perdre du poids durablement</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Fatigue chronique</strong> — Alimentation adaptée pour retrouver l'énergie</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Troubles digestifs</strong> — Ballonnements, constipation, syndrome de l'intestin irritable</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Équilibre hormonal</strong> — SOPK (syndrome des ovaires polykystiques), préménopause, déséquilibres hormonaux</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* L'approche */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Une approche personnalisée et durable
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '2rem'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Activity size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Bilan métabolique
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Analyse complète de votre métabolisme, hormones et besoins nutritionnels spécifiques.
                  </p>
                </div>

                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Leaf size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Plan alimentaire personnalisé
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Programme adapté à votre mode de vie, vos goûts et vos objectifs, sans régime restrictif.
                  </p>
                </div>
              </div>
            </div>

            {/* Cabinet médical */}
            <div style={{ marginBottom: '3rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Consultations en cabinet médical
              </h2>
              <div style={{
                background: 'linear-gradient(135deg, var(--accent-primary-light) 0%, rgba(200, 230, 216, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--accent-primary)',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                <p className="body-large" style={{ lineHeight: '1.8', marginBottom: '1rem' }}>
                  Je reçois à <strong>Vevey</strong> et <strong>Clarens (Montreux)</strong>, dans un cadre médical professionnel 
                  offrant un environnement adapté à un suivi de qualité.
                </p>
                <p className="body-large" style={{ lineHeight: '1.8' }}>
                  Consultations également disponibles <strong>en ligne</strong> pour plus de flexibilité.
                </p>
              </div>

              {/* Localisation */}
              <p className="body-large" style={{ 
                marginTop: '2rem', 
                padding: '1rem',
                background: 'var(--bg-page)',
                borderRadius: '0.75rem',
                fontWeight: 500,
                textAlign: 'center',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                Cabinet à Vevey et consultations à Clarens (Montreux), facilement accessible depuis toute la Riviera.
              </p>
            </div>
          </div>
        </section>

        <FAQ items={faqMap['nutritionniste-vevey-clarens']} />

        <RelatedPages items={relatedPagesMap['nutritionniste-vevey-clarens']} />

        {/* CTA Section */}
        <section style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '5rem 0'
        }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
              Prêt(e) à améliorer votre alimentation ?
            </h2>
            <p className="body-large" style={{ 
              color: 'white', 
              opacity: 0.95, 
              maxWidth: '700px', 
              margin: '0 auto 2.5rem' 
            }}>
              Prenez rendez-vous pour une première consultation nutritionnelle
            </p>
            <a
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary" 
              style={{ 
                background: 'white', 
                color: 'var(--accent-primary)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                textDecoration: 'none'
              }}
            >
              Réserver une consultation
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <style>{`
          .approach-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(61, 84, 70, 0.15);
            border-color: var(--accent-primary);
          }
        `}</style>
      </div>
    </>
  );
};

export default NutritionnisteVeveyClarens;
